import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { StatusBadge } from "@/components/ui/status-badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Pagination } from "@/components/ui/pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import icArrowLeft from "@/assets/icons/ic-arrow-narrow-left.svg";
import {
  UserManagementService,
  UserInquiryRes,
  IInquiryStatus,
} from "@/services/user-management-service";
import { STATUS_CONFIG_ADMIN, STATUS_CONFIG_INQUIRY } from "@/lib/utils";

const PAGE_SIZE = 10;

const ManageUserInquiryListPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [inquiries, setInquiries] = useState<UserInquiryRes[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [userName, setUserName] = useState("");

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inquiryToDelete, setInquiryToDelete] = useState<UserInquiryRes | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const [userResponse, inquiryResponse] = await Promise.all([
          UserManagementService.getById(id),
          UserManagementService.getListUserInquiries({
            user_Id: id,
            page: currentPage,
            page_size: PAGE_SIZE,
          }),
        ]);

        const userData = userResponse.data.data;
        const inquiriesData = inquiryResponse.data.data?.items ?? [];
        const meta = inquiryResponse.data.data;

        if (userData) {
          setUserName(userData.requester_name);
        }

        setInquiries(inquiriesData);
        setTotal(meta?.total ?? 0);
        setTotalPages(Math.ceil((meta?.total ?? 0) / PAGE_SIZE) || 1);
      } catch (err) {
        setError("Failed to load inquiries");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentPage]);

  const handleBack = () => {
    navigate(`/manage-user/${id}`);
  };

  const handleDeleteClick = (inquiry: UserInquiryRes) => {
    setInquiryToDelete(inquiry);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!inquiryToDelete || !id) return;

    try {
      setDeleteLoading(true);
      await UserManagementService.deleteUserInquiry(id, inquiryToDelete.id);
      toast({
        title: "문의가 삭제되었습니다.",
      });
      // Refresh the list
      const inquiryResponse = await UserManagementService.getListUserInquiries({
        user_Id: id,
        page: currentPage,
        page_size: PAGE_SIZE,
      });
      const items = inquiryResponse.data.data?.items ?? [];
      const meta = inquiryResponse.data.data;

      if (items.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        setInquiries(items);
        setTotal(meta?.total ?? 0);
        setTotalPages(Math.ceil((meta?.total ?? 0) / PAGE_SIZE) || 1);
      }
    } catch (err) {
      toast({
        title: "삭제 실패",
        description: "문의 삭제 중 오류가 발생했습니다.",
        variant: "destructive",
      });
      console.error(err);
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setInquiryToDelete(null);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG_INQUIRY[status];
    return (
      <div
        className={`inline-flex items-center gap-1 h-6 justify-center rounded  ${config.bg} ${config.text}  ${config.padding}`}
      >
        <p className="text-[14px] leading-[20px] font-normal tracking-[-0.01em]">{config.label}</p>
      </div>
    );
  };

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div className="min-h-screen" style={{ display: loading ? "none" : "block" }}>
        {/* Header */}
        <div className="flex items-center gap-1 mb-6">
          <Button
            onClick={handleBack}
            variant="ghost"
            size="icon"
            className="w-12 h-12  flex items-center justify-center"
          >
            <img src={icArrowLeft} alt="Back" />
          </Button>
          <h1 className="text-[20px] font-bold text-[#000000]">{userName}님의 문의 이력</h1>
        </div>

        {/* Body Card */}
        <div className="bg-white rounded-[16px]">
          {/* Table */}
          <div className="border border-[rgba(0,0,0,0.09)] rounded-[4px] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#EBEBEB] border-b border-[rgba(0,0,0,0.09)]">
                  <TableHead>제목</TableHead>
                  <TableHead>생성일</TableHead>
                  <TableHead>상태</TableHead>
                  <TableHead>관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center p-8 text-[#8d8d8d]">
                      문의가 없습니다
                    </TableCell>
                  </TableRow>
                ) : (
                  inquiries.map((inquiry) => (
                    <TableRow
                      key={inquiry.id}
                      className="border-b border-[rgba(0,0,0,0.09)] last:border-b-0"
                    >
                      <TableCell className="p-3">
                        <span className="text-[16px] text-[#202020]">{inquiry.title}</span>
                      </TableCell>
                      <TableCell className="p-3">
                        <span className="text-[16px] text-[#202020]">
                          {formatDate(inquiry.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="p-3">{getStatusBadge(inquiry.status)}</TableCell>
                      <TableCell className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-[80px] h-[32px]  text-[12px] font-bold text-[#000000] bg-[#F7DDCF]"
                            onClick={() => handleDeleteClick(inquiry)}
                          >
                            삭제
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          variant="danger"
          title="문의 삭제 확인"
          description={`해당 문의를 삭제하시겠습니까?`}
          confirmText="네"
          cancelText="아니요"
          onConfirm={handleDeleteConfirm}
          loading={deleteLoading}
        />
      </div>
    </>
  );
};

export default ManageUserInquiryListPage;
